// SCR-STAFF-414 — گزارش مطالبات (US-BIL-151): سنی مطالبات به تفکیک بدهکار (مراجعه‌کننده، سازمان، اعتبار شخص ثالث) در بازه‌های جاری، ۰–۳۰، ۳۱–۶۰،
// ۶۱–۹۰ و بیش از ۹۰ روز؛ اقساط سررسید هفت روز آینده، چک‌های در جریان و معوقات (اقساط و چک برگشتی) با پیوند به پروفایل مالی.
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { unwrap, type BilReceivableItemView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, JalaliDatePickerRHF, ListPage, LoadingState, toBusinessDate } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { Amount, bilKeys, financePath, SCR_STAFF_414 } from './common';

const BUCKETS = ['not_due', '0_30', '31_60', '61_90', '90_plus'] as const;

function ItemsTable({ titleKey, items, testId }: { titleKey: string; items: BilReceivableItemView[]; testId: string }) {
  const { t } = useTranslation('bil');
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={testId}>
      <Typography variant="subtitle1" gutterBottom>
        {t(titleKey)} ({faDigits(String(items.length))})
      </Typography>
      {items.length === 0 ? (
        <Typography color="text.secondary">{t('bil:receivables.none')}</Typography>
      ) : (
        <Table size="small" aria-label={t(titleKey)}>
          <TableHead>
            <TableRow>
              <TableCell>{t('bil:deposits.columns.patient')}</TableCell>
              <TableCell>{t('bil:receivables.kind')}</TableCell>
              <TableCell>{t('bil:receivables.reference')}</TableCell>
              <TableCell>{t('bil:installments.due')}</TableCell>
              <TableCell>{t('bil:cashier.amount')}</TableCell>
              <TableCell>{t('bil:installments.days_overdue')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`${item.kind}-${item.id}`} data-testid="bil-receivable-row" data-kind={item.kind}>
                <TableCell>
                  <Link component={RouterLink} to={financePath(item.patientId)}>
                    {item.patientName ?? '—'}
                  </Link>
                </TableCell>
                <TableCell>{t(`bil:receivables.kinds.${item.kind}`, { defaultValue: item.kind })}</TableCell>
                <TableCell>
                  <bdi dir="ltr">{item.reference}</bdi>
                </TableCell>
                <TableCell>
                  <JalaliDate value={item.dueDate} />
                </TableCell>
                <TableCell>
                  <Amount money={item.amount} />
                </TableCell>
                <TableCell>{Number(item.daysOverdue) > 0 ? faDigits(String(item.daysOverdue)) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export function ReceivablesReportPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const today = toBusinessDate(new Date());
  const { control, watch } = useForm<{ asOf: string | null }>({ defaultValues: { asOf: today } });
  const asOf = watch('asOf') ?? today;
  const report = useQuery({
    queryKey: bilKeys.receivables(session?.tenantId, asOf),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/reports/receivables', { params: { query: { asOf } } })),
  });
  const data = report.data;
  return (
    <ListPage
      screenId={SCR_STAFF_414}
      titleKey="bil:receivables.title"
      subtitleKey="bil:receivables.subtitle"
      filters={<JalaliDatePickerRHF control={control} name="asOf" labelKey="bil:deferred.as_of" />}
    >
      {report.isPending ? <LoadingState rows={4} /> : null}
      {report.error ? <ErrorState error={report.error} onRetry={() => void report.refetch()} /> : null}
      {data ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-receivables-aging">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">{t('bil:receivables.total')}</Typography>
              <Amount money={data.total} testId="bil-receivables-total" />
            </Stack>
            <Table size="small" aria-label={t('bil:receivables.aging')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('bil:receivables.debtor')}</TableCell>
                  {BUCKETS.map((bucket) => (
                    <TableCell key={bucket}>{t(`bil:receivables.buckets.${bucket}`)}</TableCell>
                  ))}
                  <TableCell>{t('bil:receivables.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.debtors.map((debtor) => (
                  <TableRow key={debtor.debtorType} data-testid="bil-receivables-debtor" data-debtor={debtor.debtorType}>
                    <TableCell>{t(`bil:receivables.debtors.${debtor.debtorType}`, { defaultValue: debtor.debtorType })}</TableCell>
                    {BUCKETS.map((bucket) => {
                      const cell = debtor.buckets.find((item) => item.bucket === bucket);
                      return (
                        <TableCell key={bucket} data-bucket={bucket}>
                          {cell && cell.amount.amount > 0 ? <Amount money={cell.amount} /> : '—'}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      <Amount money={debtor.total} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">
              {t('bil:receivables.as_of')} <JalaliDate value={data.asOf} />
            </Typography>
          </Paper>
          <ItemsTable titleKey="bil:receivables.due_installments" items={data.dueInstallments} testId="bil-receivables-due" />
          <ItemsTable titleKey="bil:receivables.cheques" items={data.chequesInProgress} testId="bil-receivables-cheques" />
          <ItemsTable titleKey="bil:receivables.overdue" items={data.overdue} testId="bil-receivables-overdue" />
        </Stack>
      ) : null}
    </ListPage>
  );
}
